import type { Theme } from '@mui/material';

/**
 * Design tokens for the Organization Profile page, lifted from the Profile Hub
 * design (`F4Ibkdv…`). Most map 1:1 to the MUI light palette (primary,
 * accent.green/blue, error, text.*, background.*); the amber/low-confidence
 * scale and the neutral surface tones are not in the theme, so they live here
 * as a small local map rather than polluting the global palette.
 */
export const ORG_TOKENS = {
  // text
  text1: '#0A0A0A',
  text2: '#5C5C5C',
  text3: '#8F8F8F',
  text4: '#ADADAD',
  // borders / surfaces
  border: '#EBEBEB',
  borderStrong: '#E0E0E0',
  bgApp: '#EFEFF2',
  bgPaper: '#FFFFFF',
  bgSunken: '#F5F5F7',
  bgSubtle: '#FAFAFA',
  // confidence — green (high)
  green700: '#1B9E5C',
  green200: '#DDFBEB',
  green100: '#F0FDF6',
  // confidence — blue (edited / medium)
  blue700: '#2178EF',
  blue200: '#DAEEFF',
  blue100: '#EFF8FF',
  // confidence — amber (low)
  amber700: '#B45309',
  amber500: '#F59E0B',
  amber200: '#FDE68A',
  amber100: '#FEF3C7',
  amberBg: '#FFFCF4',
  // reference PDF badge
  pdfRed: '#D14343',
  // validation error text
  error: '#E5484D',
  // radii
  radiusSm: 8,
  radiusMd: 12,
  radiusLg: 16,
} as const;

/**
 * Confidence display level. No longer rendered as per-field chrome on this page
 * (the Organization Profile design shows a clean label/value), but still used
 * to derive a category's "pending review" status from a low-confidence value.
 */
export type ConfidenceLevel = 'high' | 'medium' | 'low' | 'edited' | 'empty';

/**
 * The subtle rounded card that wraps a field in read-only mode (label + value),
 * matching the approved-category mockup. Colors resolve from the MUI theme
 * palette (sx palette paths).
 */
export const readCardSx = {
  backgroundColor: 'grey.200',
  border: 1,
  borderColor: 'grey.300',
  borderRadius: `${ORG_TOKENS.radiusSm}px`,
  px: { xs: 1.25, sm: 1.75 },
  py: { xs: 1.25, sm: 1.5 },
} as const;

type BannerTone = 'green' | 'blue' | 'grey' | 'yellow';

/** Which step of each color scale supplies the banner's border. */
const BANNER_BORDER: Record<BannerTone, number> = {
  green: 300,
  blue: 200,
  grey: 300,
  yellow: 300,
};

/**
 * Soft single-tone gradient used by the page's status banners — the editing /
 * read-only / saving "save box" and the review banner. The color tint sits on
 * the leading edge and fades to paper, matching the mockups; the direction
 * follows the active text direction so the tint stays leading-aligned in RTL.
 *
 * NB: the gradient is intentionally percentage-free AND uses a fixed `to right`.
 * The app's RTL stylis plugin (cssjanus) mirrors gradient stop percentages
 * (e.g. `0%`→`100%`), which corrupts the gradient in Arabic — but it leaves a
 * percentage-free gradient (and its `to right` keyword) untouched. That keeps
 * the colored tint on the left and fading to paper on the right in both LTR and
 * Arabic, repeating the tint stop so it stays dominant across the leading half.
 */
export function bannerGradientSx(theme: Theme, tone: BannerTone) {
  const scale = theme.palette[tone] as unknown as Record<number, string>;
  return {
    background: `linear-gradient(to right, ${scale[100]}, ${scale[100]}, ${theme.palette.background.paper})`,
    border: `1px solid ${scale[BANNER_BORDER[tone]]}`,
  } as const;
}

/**
 * The bordered input container used in edit mode. A dirty (unsaved) field gets
 * the light-blue tint + blue border from the mockup; otherwise it's a plain
 * white box. Colors resolve from the MUI theme palette (sx palette paths).
 */
export function editBoxSx(dirty?: boolean) {
  return {
    border: 1,
    borderColor: dirty ? 'blue.200' : 'grey.400',
    backgroundColor: dirty ? 'blue.100' : 'background.paper',
    borderRadius: `${ORG_TOKENS.radiusSm}px`,
  } as const;
}
